import { useEffect } from "react";
import { brand, brandId } from "../brand.config";

/* ── Keyframe CSS injected once into <head> ─────────────────────── */
const CSS = `
@keyframes arcCircleDraw {
  from { stroke-dashoffset: 283; }
  to   { stroke-dashoffset: 0;   }
}
@keyframes arcPlayDraw {
  from { opacity: 0; stroke-dashoffset: 120; }
  to   { opacity: 1; stroke-dashoffset: 0;   }
}
@keyframes pulseLineDraw {
  from { stroke-dashoffset: 1000; }
  to   { stroke-dashoffset: 0;    }
}
@keyframes splashLabelIn {
  from { opacity: 0; letter-spacing: 0.6em; }
  to   { opacity: 1; letter-spacing: 0.3em; }
}
@keyframes splashOut {
  0%   { opacity: 1; }
  70%  { opacity: 1; }
  100% { opacity: 0; pointer-events: none; }
}
.splash-root {
  animation: splashOut 2s ease-out forwards;
}
`;

/* ── ArcPlayer splash: circle draws itself, then play icon fades in ── */
const ArcSplash = () => (
  <svg width="150" height="150" viewBox="0 0 150 150" fill="none" aria-hidden="true">
    {/* Outer thin ring */}
    <circle cx="75" cy="75" r="60"
      stroke="rgba(0,191,255,0.12)" strokeWidth="1" />
    {/* Animated main arc */}
    <circle cx="75" cy="75" r="44"
      stroke="#00BFFF" strokeWidth="1.5"
      strokeDasharray="276.5" strokeDashoffset="276.5"
      strokeLinecap="round"
      style={{ animation: "arcCircleDraw 0.85s cubic-bezier(0.4,0,0.2,1) 0.15s forwards" }}
    />
    {/* Play triangle */}
    <polygon points="63,56 63,94 101,75"
      stroke="#00BFFF" strokeWidth="1.5"
      strokeLinejoin="round"
      strokeDasharray="120" strokeDashoffset="120"
      style={{ animation: "arcPlayDraw 0.45s ease-out 0.9s forwards", opacity: 0 }}
    />
  </svg>
);

/* ── PulsePlayer splash: EKG line draws left-to-right ───────────── */
const PulseSplash = () => (
  <svg width="320" height="72" viewBox="0 0 320 72"
    fill="none" overflow="visible" aria-hidden="true">
    <defs>
      <linearGradient id="splashPG" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%"   stopColor="#00E5FF" />
        <stop offset="100%" stopColor="#A855F7" />
      </linearGradient>
    </defs>
    {/* Flat guide line */}
    <line x1="0" y1="36" x2="320" y2="36"
      stroke="rgba(0,229,255,0.08)" strokeWidth="1" />
    {/* Animated EKG pulse */}
    <polyline
      points="0,36 44,36 58,11 72,61 86,21 100,51 112,36 148,36 168,7 186,65 202,23 216,36 278,36 295,20 310,52 320,36"
      stroke="url(#splashPG)" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      pathLength="1000"
      strokeDasharray="1000" strokeDashoffset="1000"
      style={{ animation: "pulseLineDraw 1s cubic-bezier(0.4,0,0.2,1) 0.1s forwards" }}
    />
  </svg>
);

/* ── Main component ─────────────────────────────────────────────── */
const SplashScreen = ({ onDone }) => {
  useEffect(() => {
    // Fallback: always resolve after 2.2s even if animationend fires late
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [onDone]);

  const isArc   = brandId === "arcplayer";
  const isPulse = brandId === "pulseplayer";

  return (
    <>
      <style>{CSS}</style>
      <div
        className="splash-root"
        onAnimationEnd={onDone}
        style={{
          position:        "fixed",
          inset:           0,
          zIndex:          9999,
          backgroundColor: brand.bg,
          display:         "flex",
          flexDirection:   "column",
          alignItems:      "center",
          justifyContent:  "center",
          gap:             "20px",
          pointerEvents:   "none",
        }}
      >
        {isArc && (
          <>
            <ArcSplash />
            <span style={{
              color:       "#00BFFF",
              fontSize:    "11px",
              fontFamily:  brand.fontFamily,
              fontWeight:  600,
              opacity:     0,
              animation:   "splashLabelIn 0.5s ease-out 1.1s forwards",
            }}>
              ARCPLAYER
            </span>
          </>
        )}

        {isPulse && (
          <>
            <PulseSplash />
            <span style={{
              background:             "linear-gradient(135deg,#00E5FF,#A855F7)",
              WebkitBackgroundClip:   "text",
              WebkitTextFillColor:    "transparent",
              backgroundClip:         "text",
              fontSize:               "11px",
              fontFamily:             brand.fontFamily,
              fontWeight:             600,
              opacity:                0,
              animation:              "splashLabelIn 0.5s ease-out 1.2s forwards",
            }}>
              PULSEPLAYER
            </span>
          </>
        )}
      </div>
    </>
  );
};

export default SplashScreen;
